import { ActionType, createAction, createAsyncAction } from 'typesafe-actions';
import { DataFetchingThunkAction } from '../types';
import { convertListingPageStateToUrl } from '../../components/ListingPage/services/listingPageUrlService';
import { secondaryProductsPathMap } from '../../../common/apiAdapters/shopApiUrlService';
import {
    FetchAction,
    DefaultErrorPayload,
    SuccessFetchActionPayload,
    createDataFetchActions,
} from '../dataFetching/generators';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import {
    ProductsResponse,
    ListingPageUrlState,
    SecondaryProductsIdentifier,
    PriceFormatConfigOptions,
    Options,
} from './types';

export const fetchProductsActions = createAsyncAction(
    '@PRODUCTS/FETCH_START',
    '@PRODUCTS/FETCH_SUCCESS',
    '@PRODUCTS/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ProductsResponse>, DefaultErrorPayload>();

export const fetchProductsDataIfNeeded = (
    urlState: ListingPageUrlState,
    reducerPayloadOptions: Options
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    const identifier = convertListingPageStateToUrl(urlState);
    const { includeLooksInfusions } = reducerPayloadOptions;
    if (identifier === getState().products.identifier) {
        return;
    }
    dispatch(fetchProductsActions.request({ identifier }));
    try {
        const products = await apiService.shopApi.getProductsForListingState(urlState, includeLooksInfusions);
        dispatch(fetchProductsActions.success({ data: products, identifier, reducerPayloadOptions }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductsDataIfNeeded');
        dispatch(fetchProductsActions.failure({ error: err, identifier }));
    }
};

export const fetchSecondaryProductsActions = createAsyncAction(
    '@SECONDARY_PRODUCTS/FETCH_START',
    '@SECONDARY_PRODUCTS/FETCH_SUCCESS',
    '@SECONDARY_PRODUCTS/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ProductsResponse>, DefaultErrorPayload>();

export type SecondaryProductsActionsType = ActionType<typeof fetchSecondaryProductsActions>;

export const fetchSecondaryProductsDataIfNeeded = (
    identifier: SecondaryProductsIdentifier,
    reducerPayloadOptions: PriceFormatConfigOptions
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    if (getState().secondaryProducts.products[identifier].isFetching) {
        return;
    }
    dispatch(fetchSecondaryProductsActions.request({ identifier }));
    try {
        const path = secondaryProductsPathMap[identifier];
        const products = await apiService.shopApi.getProducts(path, {
            page: 1,
        });
        dispatch(fetchSecondaryProductsActions.success({ data: products, identifier, reducerPayloadOptions }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchSecondaryProductsDataIfNeeded');
        dispatch(fetchSecondaryProductsActions.failure({ error: err, identifier }));
    }
};

export const newRandomizedProductsActions = createDataFetchActions(
    '@HOME_PAGE/NEW_PRODUCTS',
    // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars
    ({ apiService }) => (pageSize: number, _: PriceFormatConfigOptions) => {
        return apiService.shopApi.getHomePageNewProducts(pageSize);
    },
    // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars
    _ => '@HOME_PAGE/NEW_PRODUCTS',
    (_, priceFormatOptions) => priceFormatOptions
);

export type NewProductsRandomizedActions = ActionType<typeof newRandomizedProductsActions.actions>;
export const fetchNewRandomizedProducts = newRandomizedProductsActions.thunk;

export const bestSellerProductsActions = createDataFetchActions(
    '@HOME_PAGE/BEST_SELLER_PRODUCTS',
    // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars
    ({ apiService }) => (count: number, _: PriceFormatConfigOptions) => {
        return apiService.shopApi.getHomePageBestSellerProducts(count);
    },
    // eslint-disable-next-line  no-unused-vars, @typescript-eslint/no-unused-vars
    _ => '@HOME_PAGE/BEST_SELLER_PRODUCTS',
    (_, priceFormatOptions) => priceFormatOptions
);

export type BestSellerProductsActions = ActionType<typeof bestSellerProductsActions.actions>;
export const fetchBestSellerProducts = bestSellerProductsActions.thunk;

export const fetchProductRelativesActions = createAsyncAction(
    '@PRODUCT_DETAILS/RELATIVES/FETCH_START',
    '@PRODUCT_DETAILS/RELATIVES/FETCH_SUCCESS',
    '@PRODUCT_DETAILS/RELATIVES/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ProductsResponse>, DefaultErrorPayload>();

export type ProductRelativesActionsType = ActionType<typeof fetchProductRelativesActions>;

export const fetchProductRelativesIfNeeded = (
    urlKey: string,
    reducerPayloadOptions: PriceFormatConfigOptions
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    if (getState().productRelatives.identifier === urlKey) {
        return;
    }
    dispatch(fetchProductRelativesActions.request({ identifier: urlKey }));
    try {
        const products = await apiService.shopApi.getProductRelativesForDetailPage(urlKey);
        dispatch(fetchProductRelativesActions.success({ data: products, identifier: urlKey, reducerPayloadOptions }));
    } catch (err) {
        log404ErrorAsWarning(err, urlKey, logger, 'fetchProductRelativesIfNeeded');
        dispatch(fetchProductRelativesActions.failure({ error: err, identifier: urlKey }));
    }
};

export const setIsMoodImageHovered = createAction('@PRODUCTS/SET_IS_MOOD_IMAGE_HOVERED', action => {
    return (isHovered: boolean) => action({ isHovered });
});

export type ProductsActionsType = ActionType<typeof fetchProductsActions | typeof setIsMoodImageHovered>;
